import { createContext, useContext, useState, useEffect, type ReactNode } from 'react'
import type { AgeGroup } from '@/types'

type ThemeMode = 'light' | 'dark' | 'system'

interface ThemeContextType {
  ageTheme: AgeGroup
  setAgeTheme: (theme: AgeGroup) => void
  mode: ThemeMode
  setMode: (mode: ThemeMode) => void
  isDark: boolean
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined)

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [ageTheme, setAgeTheme] = useState<AgeGroup>(() => {
    if (typeof window !== 'undefined') {
      return (localStorage.getItem('ageTheme') as AgeGroup) || 'adult'
    }
    return 'adult'
  })

  const [mode, setMode] = useState<ThemeMode>(() => {
    if (typeof window !== 'undefined') {
      return (localStorage.getItem('themeMode') as ThemeMode) || 'light'
    }
    return 'light'
  })

  const [isDark, setIsDark] = useState(false)

  // Aplicar tema de edad
  useEffect(() => {
    const root = document.documentElement
    root.classList.remove('theme-children', 'theme-adult')
    root.classList.add(`theme-${ageTheme}`)
    localStorage.setItem('ageTheme', ageTheme)
  }, [ageTheme])

  // Aplicar modo oscuro/claro
  useEffect(() => {
    const root = document.documentElement

    if (mode === 'system') {
      const systemDark = window.matchMedia('(prefers-color-scheme: dark)').matches
      setIsDark(systemDark)
      root.classList.toggle('dark', systemDark)
    } else {
      const dark = mode === 'dark'
      setIsDark(dark)
      root.classList.toggle('dark', dark)
    }

    localStorage.setItem('themeMode', mode)
  }, [mode])

  // Escuchar cambios en preferencia del sistema
  useEffect(() => {
    if (mode !== 'system') return

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    const handler = (e: MediaQueryListEvent) => {
      setIsDark(e.matches)
      document.documentElement.classList.toggle('dark', e.matches)
    }

    mediaQuery.addEventListener('change', handler)
    return () => mediaQuery.removeEventListener('change', handler)
  }, [mode])

  const value: ThemeContextType = {
    ageTheme,
    setAgeTheme,
    mode,
    setMode,
    isDark
  }

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  )
}

export function useTheme() {
  const context = useContext(ThemeContext)
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider')
  }
  return context
}
